import { SpanStatusCode, trace } from '@opentelemetry/api'
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions'
import { W3CTraceContextPropagator } from '@opentelemetry/core'

import { OTEL_ENABLED, OTEL_ENDPOINT } from './constants/otel'
import { GIT_SHA } from './constants/version'

// Propagate W3C trace context only to first-party Flowhub API hosts — not to every
// origin (was [/.*/], which leaked the traceparent header to third parties like
// Google Sign-In / LogRocket). Matches api.flowhub.com + api.<env>.flowhub.{in,co}. INFRA-245.
// The trailing (?:[:/]|$) anchors the host end so a look-alike like
// api.flowhub.com.evil.com does NOT match and leak the header.
const FLOWHUB_API_HOSTS =
  /^https:\/\/api\.([a-z0-9-]+\.)*flowhub\.(com|in|co)(?:[:/]|$)/

// LogRocket's own telemetry endpoints — don't instrument them; their failures would
// otherwise pollute the FlowhubMaui failed-transaction-rate alert. INFRA-245.
const IGNORED_REQUEST_URLS = [/r\.lr-intake\.com/, /r\.logr-ingest\.com/]

// Benign client-side errors the old Elastic agent dropped via `ignoreTransactions`
// (fetch aborts on navigation, a benign modal focus error). Keep them out of the
// error-count signal — only record genuine failures. INFRA-245.
const IGNORED_ERROR_PATTERNS = [
  /AbortError/i,
  /Fetch is aborted/i,
  /initialFocus.* refers to no known node/i,
]

export const initOtel = () => {
  if (!OTEL_ENABLED || !OTEL_ENDPOINT) {
    console.log('OTel disabled')
    return
  }

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: 'FlowhubMaui',
      'service.version': GIT_SHA,
    }),
    spanProcessors: [
      new BatchSpanProcessor(
        new OTLPTraceExporter({ url: `${OTEL_ENDPOINT}/v1/traces` })
      ),
    ],
  })

  provider.register({
    contextManager: new ZoneContextManager(),
    propagator: new W3CTraceContextPropagator(),
  })

  registerInstrumentations({
    instrumentations: [
      getWebAutoInstrumentations({
        '@opentelemetry/instrumentation-fetch': {
          propagateTraceHeaderCorsUrls: [FLOWHUB_API_HOSTS],
          ignoreUrls: IGNORED_REQUEST_URLS,
          // OTel JS doesn't set CLIENT span status to ERROR on HTTP 4xx/5xx
          // (open spec-compliance gap — opentelemetry-js#3564), so we do it here.
          // This is what gives the FlowhubMaui failed-transaction-rate alert its
          // signal (≈ Elastic apm.transaction_error_rate). 4xx + 5xx on a CLIENT
          // span are both errors per OTel semantic conventions.
          applyCustomAttributesOnSpan: (span, _request, response) => {
            const status = (response as { status?: number }).status
            if (typeof status === 'number' && status >= 400) {
              span.setStatus({
                code: SpanStatusCode.ERROR,
                message: `HTTP ${status}`,
              })
            }
          },
        },
        '@opentelemetry/instrumentation-xml-http-request': {
          propagateTraceHeaderCorsUrls: [FLOWHUB_API_HOSTS],
          ignoreUrls: IGNORED_REQUEST_URLS,
          applyCustomAttributesOnSpan: (span, xhr) => {
            if (xhr.status >= 400) {
              span.setStatus({
                code: SpanStatusCode.ERROR,
                message: `HTTP ${xhr.status}`,
              })
            }
          },
        },
      }),
    ],
  })

  // Capture uncaught JS errors + unhandled promise rejections as ERROR spans so
  // they feed the FlowhubMaui error-count alert (≈ Elastic apm.error_rate). The
  // OTel-native @opentelemetry/instrumentation-web-exception emits LOGS, not spans,
  // which wouldn't reach the spanmetrics→GMP pipeline — hence spans here.
  const errorTracer = trace.getTracer('browser-errors')
  const isIgnoredError = (err: Error) =>
    IGNORED_ERROR_PATTERNS.some(
      (re) => re.test(err.name) || re.test(err.message)
    )
  const recordError = (error: unknown, source: string) => {
    const err =
      error instanceof Error ? error : new Error(String(error ?? 'unknown'))
    if (isIgnoredError(err)) return
    const span = errorTracer.startSpan(source)
    span.recordException(err)
    span.setStatus({ code: SpanStatusCode.ERROR, message: err.message })
    span.end()
  }
  window.addEventListener('error', (event) =>
    recordError(event.error ?? event.message, 'window.onerror')
  )
  window.addEventListener('unhandledrejection', (event) =>
    recordError(event.reason, 'unhandledrejection')
  )

  console.log('OTel initialized')
}
